import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import {
  PilotIdByUfFieldId,
  resolvePilotFieldId,
} from '~/features/advanced-search/field-order/pilotFieldOrder';
import { SearchFilter } from '~/types/DashboardV2';

/**
 * Locate the Evisearch catalog entry for a saved query filter id.
 *
 * Saved Custom AI / search queries may still use Pilot ids (`DYN_<id>` or a
 * bare number) while `GET /filters` advertises Unified Fields as bare UUIDs.
 * Exact id match is tried first, then the UF client-field id, then the Pilot
 * id join used by field ordering.
 */
export const findSearchFilter = (
  searchFilters: SearchFilter[],
  queryFieldId: FieldId | number | undefined | null,
  pilotIdByUfFieldId: PilotIdByUfFieldId = new Map(),
): SearchFilter | undefined => {
  if (queryFieldId == null || queryFieldId === '') {
    return undefined;
  }
  const queryId = String(queryFieldId);

  const exact = searchFilters.find((filter) => String(filter.id) === queryId);
  if (exact) {
    return exact;
  }

  const byUf = searchFilters.find((filter) => {
    const ufId = filter.uf_client_field_id ?? filter.ufFieldId;
    return ufId != null && String(ufId) === queryId;
  });
  if (byUf) {
    return byUf;
  }

  const queryPilotId =
    resolvePilotFieldId(queryId, pilotIdByUfFieldId) ??
    (/^\d+$/.test(queryId) ? queryId : undefined);
  if (!queryPilotId) {
    return undefined;
  }

  return searchFilters.find((filter) => {
    const catalogPilotId = resolvePilotFieldId(
      String(filter.id),
      pilotIdByUfFieldId,
    );
    return catalogPilotId === queryPilotId;
  });
};

export const unmatchedSearchFilter = (filter: Filter): SearchFilter => {
  const operatorId = filter.operatorId ?? 'equals';
  return {
    id: String(filter.fieldId),
    label: String(filter.fieldId),
    type: filter.fieldType || 'text',
    section: '',
    settings: {},
    operators: [
      {
        id: operatorId,
        label: operatorId,
        cardinality: 1,
      },
    ],
    is_chartable: false,
    is_requestable_column: false,
  };
};
